import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { HoldersTable } from '../HoldersTable';
import { TransactionsTable } from '../TransactionsTable';
import { Pagination } from '../Pagination';
import { AddressBadge, TxHashBadge } from '../AddressBadge';
import { ArrowUpDown, Repeat, List, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { formatNumber } from '../../lib/utils';

const SWAPS_LIMIT_OPTIONS = [20, 50, 100];

export const NetworkActivitySection = ({
  autoUpdateEnabled,
  swapsTotal,
  transactionsTotal,
  allHolders,
  swapsLimit,
  setSwapsLimit,
  setSwapsOffset,
  swapsLoading,
  recentSwaps,
  getSwapDirection,
  swapsOffset,
  transactionsLoading,
  allTransactions,
  transactionsOffset,
  setTransactionsOffset,
  transactionsLimit,
  holdersLoading,
  holdersOffset,
  setHoldersOffset,
  holdersLimit,
}) => (
  <Card id="activity" data-testid="activity-tabs">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <ArrowUpDown className="w-5 h-5" />
        Network Activity
        {autoUpdateEnabled && (
          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-500 rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            Live
          </span>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <Tabs defaultValue="swaps" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="swaps" data-testid="swaps-tab"><Repeat className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">DEX </span>Swaps ({swapsTotal.toLocaleString()})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="transactions-tab"><List className="w-4 h-4 mr-1 sm:mr-2" /><span className="hidden sm:inline">All </span>Txns ({transactionsTotal.toLocaleString()})</TabsTrigger>
          <TabsTrigger value="holders" data-testid="holders-tab"><Users className="w-4 h-4 mr-1 sm:mr-2" />Holders ({allHolders.total.toLocaleString()})</TabsTrigger>
        </TabsList>
        <TabsContent value="swaps" className="space-y-4">
          {/* Per-page selector */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page:</span>
              {SWAPS_LIMIT_OPTIONS.map(opt => (
                <Button
                  key={opt}
                  variant={swapsLimit === opt ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => { setSwapsLimit(opt); setSwapsOffset(0); }}
                  data-testid={`swaps-per-page-${opt}-button`}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
          {swapsLoading ? (
            <div className="text-center py-8">Loading swaps...</div>
          ) : recentSwaps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No DEX swaps found</div>
          ) : (
            <div className="table-container rounded-lg border max-h-[500px] overflow-x-auto overflow-y-auto">
              <table className="data-table min-w-[700px]">
                <thead className="sticky top-0 bg-background z-10">
                  <tr>
                    <th className="whitespace-nowrap">Tx Hash</th>
                    <th className="whitespace-nowrap">Wallet</th>
                    <th className="whitespace-nowrap">Type</th>
                    <th className="whitespace-nowrap">Pool</th>
                    <th className="text-right whitespace-nowrap">KROWN</th>
                    <th className="text-right whitespace-nowrap">USD</th>
                    <th className="text-right whitespace-nowrap">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSwaps.map((swap, idx) => {
                    const direction = getSwapDirection(swap);
                    const hasUsdValue = swap.volume_usd !== null && swap.volume_usd !== 'None';
                    // Use user_wallet (the actual transaction signer) instead of sender/recipient
                    const traderAddress = swap.user_wallet || swap.sender || swap.recipient || swap.pool_address;
                    return (
                      <tr key={`${swap.tx_hash}-${swap.log_index}-${idx}`}>
                        <td><TxHashBadge hash={swap.tx_hash} truncate /></td>
                        <td><AddressBadge address={traderAddress} truncate /></td>
                        <td>
                          <Badge className={`whitespace-nowrap ${direction === 'buy' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-red-500/20 text-red-600'}`}>
                            {direction === 'buy' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                            {direction.toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          <Badge className={`text-xs whitespace-nowrap ${swap.token0_symbol === 'USDT' || swap.token1_symbol === 'USDT' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-violet-500/20 text-violet-600'}`}>
                            {swap.token0_symbol}/{swap.token1_symbol}
                          </Badge>
                        </td>
                        <td className="text-right font-mono text-sm">{formatNumber(parseFloat(swap.krown_amount || 0).toFixed(2))}</td>
                        <td className="text-right font-mono text-sm">
                          {hasUsdValue ? <span className="text-emerald-600">${formatNumber(parseFloat(swap.volume_usd || 0).toFixed(2))}</span> : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="text-right text-xs sm:text-sm text-muted-foreground whitespace-nowrap">{swap.block_time ? new Date(swap.block_time).toLocaleTimeString() : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          <Pagination total={swapsTotal} limit={swapsLimit} offset={swapsOffset} onPageChange={setSwapsOffset} />
        </TabsContent>

        {/* All Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          {transactionsLoading ? (
            <div className="text-center py-8">Loading transactions...</div>
          ) : allTransactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No transactions found</div>
          ) : (
            <TransactionsTable transactions={allTransactions} loading={transactionsLoading} />
          )}
          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {transactionsOffset + 1} - {Math.min(transactionsOffset + transactionsLimit, transactionsTotal)} of {transactionsTotal.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTransactionsOffset(Math.max(0, transactionsOffset - transactionsLimit))}
                disabled={transactionsOffset === 0}
                data-testid="transactions-prev-button"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTransactionsOffset(transactionsOffset + transactionsLimit)}
                disabled={transactionsOffset + transactionsLimit >= transactionsTotal}
                data-testid="transactions-next-button"
              >
                Next
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="holders" className="space-y-4">
          <div id="holders" />
          <HoldersTable holders={allHolders.holders} loading={holdersLoading} symbol="KROWN" />
          <Pagination total={allHolders.total} limit={holdersLimit} offset={holdersOffset} onPageChange={setHoldersOffset} />
        </TabsContent>
      </Tabs>
    </CardContent>
  </Card>
);
